/** Where a LAB / CREMATION service happens (client change, 9 Oct 2026). */
export type VisitMode = 'PICKUP' | 'HOME_VISIT' | 'AT_CENTER';

/** Labels + which modes each module offers — mirrors backend visit-modes.ts. */
export const VISIT_MODE_OPTIONS: Record<'LAB' | 'CREMATION' | 'GROOMER', { value: VisitMode; label: string }[]> = {
  LAB: [
    { value: 'AT_CENTER', label: 'On site' },
    { value: 'HOME_VISIT', label: 'Home visit' },
  ],
  CREMATION: [
    { value: 'PICKUP', label: 'Pickup' },
    { value: 'HOME_VISIT', label: 'Home visit' },
    { value: 'AT_CENTER', label: 'In location' },
  ],
  // Nov 2026: groomers choose where they work.
  GROOMER: [
    { value: 'AT_CENTER', label: 'At store' },
    { value: 'HOME_VISIT', label: 'Home visit' },
    { value: 'PICKUP', label: 'Pickup & drop' },
  ],
};

import type { VendorModuleType } from '@/features/onboarding/types';
import type { CremationServiceType } from '@/features/cremation/types';
import type { LabSampleType } from '@/features/lab/types';
import type { CatalogImage } from '@/features/catalog-image/types';
import { PET_SPECIES, type PetSpecies } from '@/features/vendor-profile/api';

export type { VendorModuleType, CremationServiceType, LabSampleType, PetSpecies };
export { PET_SPECIES };

/** Where a TRAINER session happens. v1 supports HOME_VISIT only. */
export type TrainingMode = 'TRAINING_CENTER' | 'HOME_VISIT' | 'ONLINE';

export type VendorService = {
  id: string;
  vendor_profile_id: string;
  module_type: VendorModuleType;
  name: string;
  description: string | null;
  price_inr: string; // decimal string from backend
  duration_minutes: number | null;
  training_mode: TrainingMode | null;
  cremation_service_type: CremationServiceType[];
  /** LAB / CREMATION: how the service can be done (client change, 9 Oct 2026). */
  visit_modes: VisitMode[];
  /**
   * Which pets THIS service is for, as a subset of the species the vendor accepts.
   *
   * Always explicit: the server resolves an empty list to the vendor's own species
   * when saving, so this never needs an "empty means all" reading. Empty here means
   * the vendor has declared no species at all on their profile.
   */
  species: PetSpecies[];
  lab_sample_type: LabSampleType | null;
  lab_fasting_required: boolean | null;
  lab_turnaround_hours: number | null;
  is_active: boolean;
  display_order: number;
  /**
   * Photos on the service, lowest `display_order` first.
   *
   * The list endpoint has always carried these — `vendor-service.service.ts` serializes
   * them per row — but nothing was typed to receive them, so the list drew no picture.
   */
  images: CatalogImage[];
  created_at: string;
  updated_at: string;
};

export type CreateServiceRequest = {
  module_type: VendorModuleType;
  name: string;
  description?: string;
  price_inr: number;
  duration_minutes?: number;
  training_mode?: TrainingMode;
  cremation_service_type?: CremationServiceType[];
  visit_modes?: VisitMode[];
  /**
   * Omit, or send empty, to mean "everything this vendor accepts" - the server
   * stores the resolved list. Sending a species the vendor does not accept is a 422.
   */
  species?: PetSpecies[];
  lab_sample_type?: LabSampleType | null;
  lab_fasting_required?: boolean | null;
  lab_turnaround_hours?: number | null;
  is_active?: boolean;
  display_order?: number;
};

export type UpdateServiceRequest = Partial<CreateServiceRequest>;
